import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { storageService } from './storageService';
import { getTenantHeader } from '../utils/tenantUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL;

interface ApiErrorResponse {
    detail?: string;
}

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
});

// Request interceptor
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Get token from storage
        const token = storageService.getToken();

        // If token exists, add it to request headers
        if (token) {
            config.headers.Authorization = `${token.token_type} ${token.access_token}`;
        }

        // Add X-Tenant header based on subdomain
        const tenant = getTenantHeader();
        if (tenant) {
            config.headers['X-Tenant'] = tenant;
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    },
);

// Response interceptor
api.interceptors.response.use(
    response => {
        return response;
    },
    async (error: AxiosError<ApiErrorResponse>) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const originalRequest = error.config;

        // Handle 401 Unauthorized errors
        if (error.response?.status === 404) {
            // For 404 errors, preserve the original error structure
            return Promise.reject(error);
        }

        if (error.response?.status === 401) {
            // Only redirect to login if we're not already on the login page
            // This prevents redirecting during login attempts
            if (window.location.pathname !== '/login') {
                storageService.clearAuth();
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }

        // Handle other errors
        const errorMessage = error.response?.data?.detail || 'An error occurred. Please try again.';
        return Promise.reject(new Error(errorMessage));
    },
);

export default api;
